import { createServerFn } from "@tanstack/react-start";
import { useSession } from "@tanstack/react-start/server";

type GateSession = { unlocked?: boolean };

function getSessionConfig() {
  return {
    password: process.env.CONTENT_SESSION_SECRET!,
    name: "uploads-gate",
    maxAge: 60 * 60 * 24 * 7,
    cookie: {
      httpOnly: true,
      secure: true,
      sameSite: "lax" as const,
      path: "/",
    },
  };
}

export type JourneyStep = {
  page: string;
  visited_at: string;
  event_type: string | null;
};

export type VisitorJourney = {
  visitor_id: string;
  first_seen: string;
  last_seen: string;
  pageviews: number;
  form_events: number;
  distinct_days: number;
  sources: string[];
  referrers: string[];
  landing_page: string | null;
  last_page: string | null;
  path: JourneyStep[];
  user_id: string | null;
  name: string | null;
  email: string | null;
};

export const listJourneys = createServerFn({ method: "GET" })
  .inputValidator((data: { days?: number } | undefined) => data ?? {})
  .handler(async ({ data }) => {
    const session = await useSession<GateSession>(getSessionConfig());
    if (!session.data.unlocked) {
      return { unlocked: false as const, journeys: [] as VisitorJourney[], days: 30 };
    }

    const days = Math.max(1, Math.min(365, data.days ?? 30));
    const sinceIso = new Date(Date.now() - days * 86400_000).toISOString();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const chunk = 1000;
    let from = 0;
    type Row = {
      visitor_id: string | null;
      user_id: string | null;
      visited_at: string;
      page: string | null;
      source: string | null;
      referrer: string | null;
      event_type: string | null;
    };
    const all: Row[] = [];
    while (true) {
      const { data: page, error } = await supabaseAdmin
        .from("website_visits")
        .select("visitor_id,user_id,visited_at,page,source,referrer,event_type")
        .gte("visited_at", sinceIso)
        .not("visitor_id", "is", null)
        .order("visited_at", { ascending: true })
        .range(from, from + chunk - 1);
      if (error) throw error;
      if (!page || page.length === 0) break;
      all.push(...(page as Row[]));
      if (page.length < chunk) break;
      from += chunk;
    }

    type Agg = {
      visitor_id: string;
      first_seen: string;
      last_seen: string;
      days: Set<string>;
      pageviews: number;
      form_events: number;
      sources: Set<string>;
      referrers: Set<string>;
      steps: JourneyStep[];
      user_id: string | null;
    };
    const map = new Map<string, Agg>();

    for (const v of all) {
      if (!v.visitor_id) continue;
      const day = v.visited_at.slice(0, 10);
      let a = map.get(v.visitor_id);
      if (!a) {
        a = {
          visitor_id: v.visitor_id,
          first_seen: v.visited_at,
          last_seen: v.visited_at,
          days: new Set(),
          pageviews: 0,
          form_events: 0,
          sources: new Set(),
          referrers: new Set(),
          steps: [],
          user_id: v.user_id ?? null,
        };
        map.set(v.visitor_id, a);
      }
      if (v.visited_at < a.first_seen) a.first_seen = v.visited_at;
      if (v.visited_at > a.last_seen) a.last_seen = v.visited_at;
      a.days.add(day);
      if (v.event_type === "form_submit" || v.event_type === "form_fill") {
        a.form_events += 1;
      } else {
        a.pageviews += 1;
      }
      if (v.source) a.sources.add(v.source);
      if (v.referrer) a.referrers.add(v.referrer);
      a.steps.push({
        page: v.page ?? "(unknown)",
        visited_at: v.visited_at,
        event_type: v.event_type ?? null,
      });
      if (!a.user_id && v.user_id) a.user_id = v.user_id;
    }

    const journeys = [...map.values()];

    // Resolve profile info for known users
    const userIds = [...new Set(journeys.map((j) => j.user_id).filter(Boolean))] as string[];
    const profiles = new Map<string, { name: string | null; email: string | null }>();
    if (userIds.length) {
      const { data: profs } = await supabaseAdmin
        .from("profiles")
        .select("id,name,email")
        .in("id", userIds);
      for (const p of profs ?? []) {
        profiles.set(p.id as string, {
          name: (p.name as string | null) ?? null,
          email: (p.email as string | null) ?? null,
        });
      }
    }

    const result: VisitorJourney[] = journeys
      .map((a) => {
        const prof = a.user_id ? profiles.get(a.user_id) : undefined;
        const sortedSteps = [...a.steps].sort((x, y) =>
          x.visited_at < y.visited_at ? -1 : 1,
        );
        return {
          visitor_id: a.visitor_id,
          first_seen: a.first_seen,
          last_seen: a.last_seen,
          pageviews: a.pageviews,
          form_events: a.form_events,
          distinct_days: a.days.size,
          sources: [...a.sources],
          referrers: [...a.referrers],
          landing_page: sortedSteps[0]?.page ?? null,
          last_page: sortedSteps[sortedSteps.length - 1]?.page ?? null,
          path: sortedSteps,
          user_id: a.user_id,
          name: prof?.name ?? null,
          email: prof?.email ?? null,
        };
      })
      .sort((a, b) => (a.last_seen < b.last_seen ? 1 : -1));

    return { unlocked: true as const, journeys: result, days };
  });
